import {
  type ApartmentViewState,
  DEFAULT_LIMIT,
  DEFAULT_PAGE,
  hasActiveApartmentFilters,
  parseApartmentViewParams
} from './apartmentFilterUrl'

const STORAGE_KEY = 'sna_apartment_view_state'

export type PersistedApartmentView = ApartmentViewState

function canUseStorage(): boolean {
  return typeof window !== 'undefined'
}

export function loadPersistedApartmentView(): PersistedApartmentView | null {
  if (!canUseStorage()) return null
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedApartmentView
    if (!parsed || typeof parsed !== 'object') return null
    return {
      filters: parsed.filters ?? {},
      q: typeof parsed.q === 'string' ? parsed.q : '',
      page: typeof parsed.page === 'number' ? parsed.page : DEFAULT_PAGE,
      limit: typeof parsed.limit === 'number' ? parsed.limit : DEFAULT_LIMIT,
      listingType:
        parsed.listingType === 'sale' || parsed.listingType === 'rent'
          ? parsed.listingType
          : 'rent'
    }
  } catch {
    return null
  }
}

export function savePersistedApartmentView(state: PersistedApartmentView): void {
  if (!canUseStorage()) return
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    /* quota / private mode */
  }
}

export function clearPersistedApartmentView(): void {
  if (!canUseStorage()) return
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

/** True when URL carries no user-chosen filters (safe to restore from session). */
export function isBareApartmentUrl(params: URLSearchParams): boolean {
  if (params.get('ids')) return false

  const state = parseApartmentViewParams(params)
  if (hasActiveApartmentFilters(state.filters)) return false
  if (state.q.trim()) return false
  if (state.page !== DEFAULT_PAGE) return false
  if (state.limit !== DEFAULT_LIMIT) return false
  return true
}

export function hasPersistedApartmentState(
  state: PersistedApartmentView | null
): state is PersistedApartmentView {
  if (!state) return false
  return (
    hasActiveApartmentFilters(state.filters) ||
    state.q.trim().length > 0 ||
    state.page !== DEFAULT_PAGE ||
    state.limit !== DEFAULT_LIMIT
  )
}
